/* import __COLOCATED_TEMPLATE__ from './environment-config.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import type IntlService from 'ember-intl/services/intl';
import { use } from 'ember-resources/util/function-resource';
import { AsyncData } from 'embercom/resources/utils/async-data';
import type Store from '@ember-data/store';
import type Environment from 'embercom/models/environments/environment';

export interface Args {
  ruleset: { environmentIds: number[]; set: (key: string, value: unknown) => void };
  readOnly: boolean;
}

interface Signature {
  Args: Args;
}

export default class EnvironmentConfig extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare store: Store;

  @use environments = AsyncData<Environment[]>(async () => {
    return this.store.findAll('environments/environment');
  });

  get selectedEnvironmentLabel() {
    if (this.areAllEnvironmentsEnabled) {
      return this.intl.t('standalone.common.environment-config.all-environments');
    }

    let matchedLabels = (this.environments.value || [])
      .filter((env) => this.args.ruleset.environmentIds.includes(Number(env.id)))
      .map((env) => env.label);

    if (matchedLabels.length === 0) {
      return this.intl.t('standalone.common.environment-config.none-selected');
    }

    return matchedLabels.join(', ');
  }

  get areAllEnvironmentsEnabled() {
    // All environments are enabled when environmentIds is null or empty
    return !this.args.ruleset.environmentIds || this.args.ruleset.environmentIds.length === 0;
  }

  get environmentsAndStates() {
    return (this.environments.value || []).map((environment) => {
      return {
        environment,
        isOn: this.args.ruleset.environmentIds.includes(Number(environment.id)),
      };
    });
  }

  @action
  enableAllEnvironments() {
    this.args.ruleset.set('environmentIds', []);
  }

  @action
  toggleEnvironment(environment: Environment) {
    let environmentId = Number(environment.id);
    let existingIds = (this.environments.value || []).map((env) => Number(env.id));

    // Filter out stale IDs that reference deleted environments
    let currentIds = this.args.ruleset.environmentIds.filter((id) => existingIds.includes(id));

    if (currentIds.includes(environmentId)) {
      this.args.ruleset.set(
        'environmentIds',
        currentIds.filter((id) => id !== environmentId),
      );
    } else {
      this.args.ruleset.set('environmentIds', [...currentIds, environmentId]);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::StandaloneCommon::EnvironmentConfig': typeof EnvironmentConfig;
  }
}
